import React from 'react';
import { motion, type Variants } from 'framer-motion';
import {
    HeartPulse,
    Brain,
    Activity,
    Clock,
    ShieldCheck,
    Smartphone,
    Utensils,
    Droplets,
    Scale,
    CheckCircle2,
} from 'lucide-react';

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.45,
            ease: 'easeOut',
        },
    },
};

const DAILY_MOMENTS = [
    {
        icon: Utensils,
        title: 'When you are deciding what to eat',
        desc: 'LesslyKilo helps you log fast, understand your meal, and make a better next decision instead of guessing.',
    },
    {
        icon: Droplets,
        title: 'When your energy drops during the day',
        desc: 'Track hydration, spot patterns, and get simple signals that help you stay more consistent.',
    },
    {
        icon: Scale,
        title: 'When the scale moves and you do not know why',
        desc: 'See the bigger picture behind your weight trend so one number does not throw you off track.',
    },
];

const PILLARS = [
    {
        icon: Brain,
        title: 'AI That Explains, Not Just Tracks',
        desc: 'LesslyKilo turns meals, hydration, workouts, and weight into useful insight so users can act with confidence.',
    },
    {
        icon: Activity,
        title: 'Built for Real Behavior Change',
        desc: 'Daily prompts, smart feedback, and practical next steps make consistency easier than starting over.',
    },
    {
        icon: Clock,
        title: 'Designed for Everyday Use',
        desc: 'The app is built to fit busy, real-world routines with fast logging, clear feedback, and support throughout the day.',
    },
];

export default function About() {
    const surfaceClass =
        'bg-[linear-gradient(180deg,var(--color-brand-card),var(--color-brand-ai-bg))] border border-[var(--color-brand-border)] shadow-[0_18px_50px_rgba(15,23,42,0.08)]';

    return (
        <section id="about" className="relative overflow-hidden py-24 md:py-32">
            {/* Background */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.10),transparent_32%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_25%,rgba(16,185,129,0.08),transparent_28%)]" />
                <div className="absolute left-[-8%] top-20 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="absolute right-[-10%] bottom-10 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
                <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6">
                {/* Header */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="mx-auto mb-20 max-w-4xl text-center"
                >
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[var(--color-brand-primary)] shadow-[0_8px_30px_rgba(34,211,238,0.08)]">
                        <HeartPulse size={14} />
                        Why LesslyKilo exists
                    </div>

                    <h1 className="text-4xl font-black leading-[1.05] text-[var(--color-brand-text)] md:text-6xl">
                        LesslyKilo belongs in your
                        <span className="block bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] bg-clip-text text-transparent">
              daily routine, not just your downloads folder
            </span>
                    </h1>

                    <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[var(--color-brand-text-secondary)] md:text-xl">
                        Most weight-loss apps stop at tracking. LesslyKilo is built to help users understand
                        their day, make better decisions in the moment, and stay consistent when real life gets busy.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        {[
                            'Built for daily use',
                            'AI-guided decisions',
                            'Designed for sustainable habits',
                        ].map((item) => (
                            <div
                                key={item}
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)] shadow-[0_8px_24px_rgba(15,23,42,0.06)]"
                            >
                                <CheckCircle2 size={16} className="text-emerald-400" />
                                {item}
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Main story row */}
                <div className="mb-24 grid grid-cols-1 gap-8 lg:grid-cols-12">
                    {/* Left */}
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: '-80px' }}
                        className="lg:col-span-7"
                    >
                        <div className={`relative overflow-hidden rounded-[2.5rem] p-8 md:p-10 ${surfaceClass}`}>
                            <div className="pointer-events-none absolute -left-10 top-0 h-40 w-40 rounded-full bg-[var(--color-brand-primary)]/10 blur-3xl" />

                            <div className="relative z-10">
                                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-500">
                                    <Smartphone size={13} />
                                    Why it matters on your phone
                                </div>

                                <h2 className="text-3xl font-black text-[var(--color-brand-text)] md:text-4xl">
                                    The hardest part of weight loss is not knowing what to do
                                    <span className="text-[var(--color-brand-primary)]"> in the moments that matter.</span>
                                </h2>

                                <div className="mt-6 space-y-5 text-[15px] leading-8 text-[var(--color-brand-text-secondary)] md:text-base">
                                    <p>
                                        People do not struggle because they lack goals. They struggle because daily decisions pile up fast:
                                        what to eat, whether they are on track, whether the scale is moving for the right reason, and how
                                        to recover after an off day.
                                    </p>

                                    <p>
                                        Generic trackers record the numbers, but they rarely explain them. That leaves users with data,
                                        but not enough clarity to change behavior consistently.
                                    </p>

                                    <p className="font-semibold text-[var(--color-brand-text)]">
                                        LesslyKilo was built to close that gap by turning meals, hydration, workouts, and weight into
                                        simple, useful guidance that fits naturally into everyday life.
                                    </p>
                                </div>

                                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                                    <div className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] p-5">
                                        <div className="text-xs font-black uppercase tracking-[0.18em] text-[var(--color-brand-text-secondary)]">
                                            Instead of
                                        </div>
                                        <div className="mt-2 text-sm font-semibold text-[var(--color-brand-text)]">
                                            logging for the sake of logging
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-[var(--color-brand-primary)]/18 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] p-5">
                                        <div className="text-xs font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                                            LesslyKilo helps you
                                        </div>
                                        <div className="mt-2 text-sm font-semibold text-[var(--color-brand-text)]">
                                            make smarter decisions throughout the day
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right */}
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ delay: 0.08 }}
                        className="lg:col-span-5"
                    >
                        <div className={`relative h-full overflow-hidden rounded-[2.5rem] p-8 ${surfaceClass}`}>
                            <div className="pointer-events-none absolute right-0 top-0 h-40 w-40 rounded-full bg-emerald-400/10 blur-3xl" />

                            <div className="relative z-10 flex items-center gap-4">
                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--color-brand-primary)]/20 bg-[var(--color-brand-primary)]/10">
                                    <HeartPulse className="text-[var(--color-brand-primary)]" size={30} />
                                </div>

                                <div>
                                    <h3 className="text-xl font-black text-[var(--color-brand-text)]">Built from real health experience</h3>
                                    <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--color-brand-primary)]">
                                        Product-led, health-informed
                                    </p>
                                </div>
                            </div>

                            <blockquote className="relative z-10 mt-6 text-[15px] italic leading-8 text-[var(--color-brand-text-secondary)]">
                                “LesslyKilo was designed for the part of the journey that usually gets ignored — the everyday choices
                                between appointments, between weigh-ins, and between moments of motivation. That is where consistency is won or lost.”
                            </blockquote>

                            <div className="mt-8 rounded-2xl border border-emerald-400/18 bg-emerald-400/8 p-5">
                                <div className="flex items-center gap-2 text-sm font-bold text-[var(--color-brand-text)]">
                                    <ShieldCheck size={16} className="text-emerald-500" />
                                    Why users trust it
                                </div>
                                <p className="mt-2 text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                                    LesslyKilo combines structured tracking, health-aware logic, and AI guidance in one experience,
                                    helping users stay consistent without feeling overwhelmed.
                                </p>
                            </div>

                            <div className="mt-6 flex flex-wrap gap-3">
                                <div className="rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[var(--color-brand-text-secondary)]">
                                    Everyday tracking
                                </div>
                                <div className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-cyan-500">
                                    AI guidance
                                </div>
                                <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-emerald-500">
                                    Sustainable habits
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Daily routine fit */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="mb-12 text-center"
                >
                    <h2 className="text-3xl font-black text-[var(--color-brand-text)] md:text-4xl">
                        Where LesslyKilo becomes useful
                        <span className="text-[var(--color-brand-primary)]"> every single day</span>
                    </h2>
                    <p className="mx-auto mt-4 max-w-3xl text-base leading-8 text-[var(--color-brand-text-secondary)] md:text-lg">
                        The app is not just for check-ins. It is designed for the moments when people need support,
                        clarity, and a better next step.
                    </p>
                </motion.div>

                <div className="mb-24 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {DAILY_MOMENTS.map((item, idx) => {
                        const Icon = item.icon;
                        return (
                            <motion.div
                                key={item.title}
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ delay: idx * 0.08 }}
                                className={`group relative overflow-hidden rounded-[2rem] p-8 transition-all hover:border-[var(--color-brand-primary)]/25 hover:translate-y-[-4px] ${surfaceClass}`}
                            >
                                <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(34,211,238,0.05),transparent_40%,rgba(16,185,129,0.05))]" />

                                <div className="relative z-10">
                                    <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(34,211,238,0.12),rgba(16,185,129,0.10))] text-[var(--color-brand-primary)] shadow-[0_10px_24px_rgba(34,211,238,0.10)]">
                                        <Icon size={26} />
                                    </div>

                                    <h3 className="text-xl font-black text-[var(--color-brand-text)]">
                                        {item.title}
                                    </h3>

                                    <p className="mt-3 text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                                        {item.desc}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Pillars */}
                <div className="mb-12 text-center">
                    <h2 className="text-3xl font-black text-[var(--color-brand-text)] md:text-4xl">
                        What makes LesslyKilo
                        <span className="text-[var(--color-brand-primary)]"> different</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    {PILLARS.map((pillar, idx) => {
                        const Icon = pillar.icon;
                        return (
                            <motion.div
                                key={pillar.title}
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ delay: idx * 0.08 }}
                                className={`rounded-[2rem] p-8 ${surfaceClass}`}
                            >
                                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary)]">
                                    <Icon size={24} />
                                </div>

                                <h3 className="text-xl font-bold text-[var(--color-brand-text)]">
                                    {pillar.title}
                                </h3>

                                <p className="mt-3 text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                                    {pillar.desc}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}